import type { ColumnState } from 'ag-grid-community';

/**
 * The grid state a route editor's opener stashes per tab so the grid can put it
 * back when the editor returns (WAP-2025). One stash per view id; the editor
 * names the row to mark through `focusReturnState`.
 */
export interface GridReturnState {
  /** The editor URL the opener navigated to; the stash is restored only when the grid is entered from under its parent. */
  editorUrl: string;
  columnState: ColumnState[];
  filterModel: { [colId: string]: unknown } | null;
  presetId: string | null;
  /** 1-based page the grid showed when it was left. */
  page: number;
  /** Vertical scroll offset of the grid body in pixels when it was left. */
  scrollTop: number;
  /** The row to scroll to and mark on return; set by the editor, `null` until then. */
  focusId: string | null;
}

/** Query param of a grid route naming the row to scroll to and mark on load. */
export const GRID_FOCUS_QUERY_PARAM = 'focus';

const KEY_PREFIX = 'sensika.grid.';
const KEY_SUFFIX = '.return-state';
const LAST_KEY = 'sensika.grid.return-state.last';

/** The subset of `Storage` the utils touch, so a test can pass a plain map. */
export interface ReturnStateStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function returnStateKey(viewId: string): string {
  return `${KEY_PREFIX}${viewId}${KEY_SUFFIX}`;
}

export function writeReturnState(
  storage: ReturnStateStorage,
  viewId: string,
  state: GridReturnState,
): void {
  storage.setItem(returnStateKey(viewId), JSON.stringify(state));
  storage.setItem(LAST_KEY, viewId);
}

/** The stash of the view, or `null` when there is none or it does not parse. */
export function readReturnState(
  storage: ReturnStateStorage,
  viewId: string,
): GridReturnState | null {
  const raw = storage.getItem(returnStateKey(viewId));
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as Partial<GridReturnState> | null;
    if (!parsed || typeof parsed !== 'object' || typeof parsed.editorUrl !== 'string') {
      return null;
    }
    return {
      editorUrl: parsed.editorUrl,
      columnState: Array.isArray(parsed.columnState) ? parsed.columnState : [],
      filterModel:
        parsed.filterModel && typeof parsed.filterModel === 'object'
          ? parsed.filterModel
          : null,
      presetId: typeof parsed.presetId === 'string' ? parsed.presetId : null,
      page:
        typeof parsed.page === 'number' && parsed.page >= 1
          ? Math.floor(parsed.page)
          : 1,
      scrollTop:
        typeof parsed.scrollTop === 'number' && parsed.scrollTop > 0
          ? parsed.scrollTop
          : 0,
      focusId: typeof parsed.focusId === 'string' ? parsed.focusId : null,
    };
  } catch {
    return null;
  }
}

/** Reads the stash and removes it, so a later visit of the view starts fresh. */
export function takeReturnState(
  storage: ReturnStateStorage,
  viewId: string,
): GridReturnState | null {
  const state = readReturnState(storage, viewId);
  storage.removeItem(returnStateKey(viewId));
  if (storage.getItem(LAST_KEY) === viewId) {
    storage.removeItem(LAST_KEY);
  }
  return state;
}

/**
 * Names the row the editor was on in the stash written last (one tab, one
 * editor journey). Returns false when no stash is waiting.
 */
export function focusReturnState(
  storage: ReturnStateStorage,
  rowId: string,
): boolean {
  const viewId = storage.getItem(LAST_KEY);
  const state = viewId ? readReturnState(storage, viewId) : null;
  if (!viewId || !state) {
    return false;
  }
  writeReturnState(storage, viewId, { ...state, focusId: rowId });
  return true;
}

/**
 * True when `previousUrl` (the navigation the grid is entered from) sits under
 * the stash's editor route parent, so a save-as-new or a clone that moved the
 * editor to another id still counts as the same editor.
 */
export function returnsFromEditor(
  previousUrl: string | null | undefined,
  editorUrl: string,
): boolean {
  if (!previousUrl) {
    return false;
  }
  const editorPath = editorUrl.split('?')[0];
  const parent = editorPath.slice(0, editorPath.lastIndexOf('/') + 1);
  return parent.length > 1 && previousUrl.split('?')[0].startsWith(parent);
}
